/**
 * 活動開始 vs 場地租用時段（前端共用）
 * 約定：未給結束日時，結束鐘面 ≤ 開始鐘面 = 隔日（進場日仍為 ACTIVITY_DATE）
 */

export const ORGANIZER_VENUE_MAX_MINUTES = 4 * 60;
/** 後台技術上限（7 日） */
export const ADMIN_VENUE_MAX_MINUTES = 7 * 24 * 60;
const DAY_MIN = 24 * 60;

export function parseHmToMinutes(hm) {
  const s = String(hm || '').trim().slice(0, 5);
  const m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (!m) return NaN;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return NaN;
  return h * 60 + min;
}

export function minsToHm(total) {
  const t = ((Number(total) % DAY_MIN) + DAY_MIN) % DAY_MIN;
  const h = Math.floor(t / 60);
  const m = t % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function sliceYmd(v) {
  if (v == null || v === '') return null;
  const s = String(v).trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

export function ymdAddDays(ymd, days) {
  const a = sliceYmd(ymd);
  if (!a || !Number.isFinite(days)) return null;
  const d = new Date(`${a}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function daysBetweenYmd(fromYmd, toYmd) {
  const a = sliceYmd(fromYmd);
  const b = sliceYmd(toYmd);
  if (!a || !b) return NaN;
  const da = new Date(`${a}T12:00:00`);
  const db = new Date(`${b}T12:00:00`);
  return Math.round((db.getTime() - da.getTime()) / 86400000);
}

/** 結束鐘面相對開始：若結束 ≤ 開始則視為隔日（+1440） */
export function normalizeEndMinutesAfterStart(startMin, endMin) {
  if (!Number.isFinite(startMin) || !Number.isFinite(endMin)) return NaN;
  return endMin <= startMin ? endMin + DAY_MIN : endMin;
}

/** 活動時刻相對場地開始：若早於／等於開始鐘面且場地跨日，則視為隔日 */
export function normalizeActivityMinutesInVenueWindow(activityMin, venueStartMin, venueEndAbs) {
  if (!Number.isFinite(activityMin) || !Number.isFinite(venueStartMin)) return NaN;
  let at = activityMin;
  if (Number.isFinite(venueEndAbs) && venueEndAbs > DAY_MIN && at <= venueStartMin) {
    at += DAY_MIN;
  }
  return at;
}

export function absMinutesFromAnchor(anchorYmd, dateYmd, hm) {
  const mins = parseHmToMinutes(hm);
  if (!Number.isFinite(mins)) return NaN;
  const dayDiff = daysBetweenYmd(anchorYmd, dateYmd || anchorYmd);
  if (!Number.isFinite(dayDiff)) return NaN;
  return dayDiff * DAY_MIN + mins;
}

export function resolveEndDateForClock(startYmd, startHm, endHm, explicitEndYmd) {
  const explicit = sliceYmd(explicitEndYmd);
  if (explicit) return explicit;
  const vs = parseHmToMinutes(startHm);
  const ve = parseHmToMinutes(endHm);
  if (Number.isFinite(vs) && Number.isFinite(ve) && ve <= vs) {
    return ymdAddDays(startYmd, 1);
  }
  return sliceYmd(startYmd);
}

export function venueWindowDurationMinutes({
  activityDate, venueStart, venueEnd, venueEndDate,
}) {
  const startYmd = sliceYmd(activityDate);
  if (!startYmd || !venueStart || !venueEnd) return NaN;
  const endYmd = resolveEndDateForClock(startYmd, venueStart, venueEnd, venueEndDate);
  const startAbs = absMinutesFromAnchor(startYmd, startYmd, venueStart);
  const endAbs = absMinutesFromAnchor(startYmd, endYmd, venueEnd);
  if (!Number.isFinite(startAbs) || !Number.isFinite(endAbs)) return NaN;
  return endAbs - startAbs;
}

/** 是否為後台放寬過的場租窗（>4h 或結束日落在另一日） */
export function isVenueWindowExtended({
  activityDate, venueStart, venueEnd, venueEndDate,
}) {
  const startYmd = sliceYmd(activityDate);
  const endYmd = sliceYmd(venueEndDate);
  if (startYmd && endYmd && endYmd !== startYmd) return true;
  const dur = venueWindowDurationMinutes({
    activityDate, venueStart, venueEnd, venueEndDate,
  });
  return Number.isFinite(dur) && dur > ORGANIZER_VENUE_MAX_MINUTES;
}

export function isCrossMidnightHm(startHm, endHm) {
  const vs = parseHmToMinutes(startHm);
  const ve = parseHmToMinutes(endHm);
  if (!Number.isFinite(vs) || !Number.isFinite(ve)) return false;
  return ve <= vs;
}

export function defaultVenueEndFromStart(venueStart, maxMinutes = ORGANIZER_VENUE_MAX_MINUTES) {
  const vs = parseHmToMinutes(venueStart);
  if (!Number.isFinite(vs)) return '';
  return minsToHm(vs + maxMinutes);
}

/** 活動開始可選：場地開始之後到場地結束（不含）的 15 分格（不可等於場地開始；支援跨日） */
export function activityStartOptionsInVenueWindow(venueStart, venueEnd, activityDate = null, venueEndDate = null) {
  const startYmd = sliceYmd(activityDate);
  if (startYmd && venueStart && venueEnd) {
    const endYmd = resolveEndDateForClock(startYmd, venueStart, venueEnd, venueEndDate);
    const vsAbs = absMinutesFromAnchor(startYmd, startYmd, venueStart);
    const veAbs = absMinutesFromAnchor(startYmd, endYmd, venueEnd);
    if (!Number.isFinite(vsAbs) || !Number.isFinite(veAbs) || !(veAbs > vsAbs)) return [];
    const out = [];
    for (let t = vsAbs + 15; t < veAbs; t += 15) {
      out.push(minsToHm(t));
    }
    return out;
  }
  const vs = parseHmToMinutes(venueStart);
  let ve = parseHmToMinutes(venueEnd);
  if (!Number.isFinite(vs) || !Number.isFinite(ve)) return [];
  ve = normalizeEndMinutesAfterStart(vs, ve);
  if (!(ve > vs)) return [];
  const out = [];
  for (let t = vs + 15; t < ve; t += 15) {
    out.push(minsToHm(t));
  }
  return out;
}

export function clampActivityStartToVenue(activityTime, venueStart, venueEnd, activityDate = null, venueEndDate = null) {
  const opts = activityStartOptionsInVenueWindow(venueStart, venueEnd, activityDate, venueEndDate);
  if (!opts.length) return '';
  if (activityTime && opts.includes(activityTime)) return activityTime;
  return opts[0];
}

/**
 * 活動結束可選：活動開始之後的 15 分格。
 * 若有 venueEnd：上限為場地結束（不含）；支援跨日／結束日。
 * 若無 venueEnd：至當日 23:45。
 */
export function activityEndOptionsAfterStart(
  activityStart,
  venueEnd = null,
  venueStart = null,
  activityDate = null,
  venueEndDate = null,
  activityEndDate = null,
) {
  const as = parseHmToMinutes(activityStart);
  if (!Number.isFinite(as)) return [];

  const startYmd = sliceYmd(activityDate);
  if (venueEnd && startYmd && venueStart) {
    const vEndYmd = resolveEndDateForClock(startYmd, venueStart, venueEnd, venueEndDate);
    const vsAbs = absMinutesFromAnchor(startYmd, startYmd, venueStart);
    const veAbs = absMinutesFromAnchor(startYmd, vEndYmd, venueEnd);
    // 活動開始若無結束日，鐘面隔日依場地窗推
    let asAbs = absMinutesFromAnchor(startYmd, startYmd, activityStart);
    if (Number.isFinite(veAbs) && veAbs > DAY_MIN && asAbs <= vsAbs) {
      asAbs += DAY_MIN;
    }
    if (!Number.isFinite(asAbs) || !Number.isFinite(veAbs)) return [];
    const out = [];
    for (let t = asAbs + 15; t < veAbs; t += 15) {
      out.push(minsToHm(t));
    }
    return out;
  }

  if (venueEnd) {
    const vs = Number.isFinite(parseHmToMinutes(venueStart))
      ? parseHmToMinutes(venueStart)
      : as;
    let ve = parseHmToMinutes(venueEnd);
    if (!Number.isFinite(ve)) return [];
    ve = normalizeEndMinutesAfterStart(vs, ve);
    const asAbs = normalizeActivityMinutesInVenueWindow(as, vs, ve);
    const upper = ve;
    const out = [];
    for (let t = asAbs + 15; t < upper; t += 15) {
      out.push(minsToHm(t));
    }
    return out;
  }

  void activityEndDate;
  const out = [];
  for (let t = as + 15; t < DAY_MIN; t += 15) {
    out.push(minsToHm(t));
  }
  return out;
}

/** 場地設定時預設活動結束＝場地結束前 15 分；否則活動開始 +1 小時 */
export function defaultActivityEnd(venueEnd, activityStart) {
  const as = parseHmToMinutes(activityStart);
  const ve = parseHmToMinutes(venueEnd);
  if (Number.isFinite(ve) && Number.isFinite(as)) {
    const veAbs = normalizeEndMinutesAfterStart(as, ve);
    const candidate = veAbs - 15;
    if (candidate > as) return minsToHm(candidate);
  } else if (Number.isFinite(ve) && !Number.isFinite(as)) {
    return minsToHm(ve - 15);
  }
  if (Number.isFinite(as)) return minsToHm(Math.min(as + 60, DAY_MIN - 15));
  return venueEnd ? minsToHm(parseHmToMinutes(venueEnd) - 15) : '';
}

export function formatActivityTimeRange(activityStart, activityEnd, activityDate = null, activityEndDate = null) {
  const a = String(activityStart || '').trim().slice(0, 5);
  const b = String(activityEnd || '').trim().slice(0, 5);
  const startYmd = sliceYmd(activityDate);
  const endYmd = sliceYmd(activityEndDate);
  if (a && b) {
    if (startYmd && endYmd && endYmd !== startYmd) {
      return `${a} ~ ${endYmd} ${b}`;
    }
    if (isCrossMidnightHm(a, b)) return `${a} ~ ${b}（隔日）`;
    return `${a} ~ ${b}`;
  }
  return a || b || '';
}

export function venueDurationOk(
  venueStart,
  venueEnd,
  maxMinutes = ORGANIZER_VENUE_MAX_MINUTES,
  activityDate = null,
  venueEndDate = null,
) {
  if (activityDate && venueStart && venueEnd) {
    const dur = venueWindowDurationMinutes({
      activityDate, venueStart, venueEnd, venueEndDate,
    });
    if (!Number.isFinite(dur) || !(dur > 0)) return false;
    return dur <= maxMinutes;
  }
  const vs = parseHmToMinutes(venueStart);
  let ve = parseHmToMinutes(venueEnd);
  if (!Number.isFinite(vs) || !Number.isFinite(ve)) return false;
  ve = normalizeEndMinutesAfterStart(vs, ve);
  if (!(ve > vs)) return false;
  return ve - vs <= maxMinutes;
}
